import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiClient from "../../../services/apiClient";

// ACCOUNTS
export const fetchAccounts = createAsyncThunk(
  "history/fetchAccounts",
  async ({ canvasId, year, page, limit }) => {
    const { data } = await apiClient.get(
      `/canvas/${canvasId}/history/accounts?year=${year}&page=${page}&limit=${limit}`
    );
    return data;
  }
);
export const fetchAccountDetails = createAsyncThunk(
  "history/fetchAccountDetails",
  async ({ canvasId, monthKey }) => {
    const { data } = await apiClient.get(`/canvas/${canvasId}/history/accounts/${monthKey}`);

    return data;
  }
);

// SAVINGS
export const fetchSavings = createAsyncThunk(
  "history/fetchSavings",
  async ({ canvasId, year, page, limit }) => {
    const { data } = await apiClient.get(
      `/canvas/${canvasId}/history/savings?year=${year}&page=${page}&limit=${limit}`
    );
    return data;
  }
);
export const fetchSavingDetails = createAsyncThunk(
  "history/fetchSavingDetails",
  async ({ canvasId, monthKey }) => {
    const { data } = await apiClient.get(`/canvas/${canvasId}/history/savings/${monthKey}`);
    return data;
  }
);

// BUDGETS
export const fetchBudgets = createAsyncThunk(
  "history/fetchBudgets",
  async ({ canvasId, year, page, limit }) => {
    const { data } = await apiClient.get(
      `/canvas/${canvasId}/history/budgets?year=${year}&page=${page}&limit=${limit}`
    );
    return data; // { list, total }
  }
);
export const fetchBudgetDetails = createAsyncThunk(
  "history/fetchBudgetDetails",
  async ({ canvasId, monthKey }) => {
    const { data } = await apiClient.get(`/canvas/${canvasId}/history/budgets/${monthKey}`);
    return data;
  }
);

// INVESTMENTS
export const fetchInvestments = createAsyncThunk(
  "history/fetchInvestments",
  async ({ canvasId, year, page, limit }) => {
    const { data } = await apiClient.get(
      `/canvas/${canvasId}/history/investments?year=${year}&page=${page}&limit=${limit}`
    );
    return data; // { list, total }
  }
);
export const fetchInvestmentDetails = createAsyncThunk(
  "history/fetchInvestmentDetails",
  async ({ canvasId, monthKey }) => {
    const { data } = await apiClient.get(`/canvas/${canvasId}/history/investments/${monthKey}`);
    return data;
  }
);

// DEBTS
export const fetchDebts = createAsyncThunk(
  "history/fetchDebts",
  async ({ canvasId, year, page, limit }) => {
    const { data } = await apiClient.get(
      `/canvas/${canvasId}/history/debts?year=${year}&page=${page}&limit=${limit}`
    );
    return data; // { list, total }
  }
);
export const fetchDebtDetails = createAsyncThunk(
  "history/fetchDebtDetails",
  async ({ canvasId, monthKey }) => {
    const { data } = await apiClient.get(`/canvas/${canvasId}/history/debts/${monthKey}`);
    return data;
  }
);

// MORTGAGES
export const fetchMortgages = createAsyncThunk(
  "history/fetchMortgages",
  async ({ canvasId, year, page, limit }) => {
    const { data } = await apiClient.get(
      `/canvas/${canvasId}/history/mortgages?year=${year}&page=${page}&limit=${limit}`
    );
    return data; // { list, total }
  }
);
export const fetchMortgageDetails = createAsyncThunk(
  "history/fetchMortgageDetails",
  async ({ canvasId, monthKey }) => {
    const { data } = await apiClient.get(`/canvas/${canvasId}/history/mortgages/${monthKey}`);
    return data;
  }
);

const initialState = {
  accounts: { list: [], total: 0, details: null, loading: false, error: null },
  savings: { list: [], total: 0, details: null, loading: false, error: null },
  budgets: { list: [], total: 0, details: null, loading: false, error: null },
  investments: { list: [], total: 0, details: null, loading: false, error: null },
  debts: { list: [], total: 0, details: null, loading: false, error: null },
  mortgages: { list: [], total: 0, details: null, loading: false, error: null },
};

const historySlice = createSlice({
  name: "history",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Accounts
    builder
      .addCase(fetchAccounts.pending, (state) => { state.accounts.loading = true; })
      .addCase(fetchAccounts.fulfilled, (state, action) => {
        state.accounts.loading = false;
        state.accounts.list = action.payload.list || [];
        state.accounts.total = action.payload.total || 0;
      })
      .addCase(fetchAccounts.rejected, (state, action) => {
        state.accounts.loading = false; state.accounts.error = action.error.message;
      })
      .addCase(fetchAccountDetails.pending, (state) => { state.accounts.loading = true; })
      .addCase(fetchAccountDetails.fulfilled, (state, action) => {
        state.accounts.loading = false; state.accounts.details = action.payload;
      })
      .addCase(fetchAccountDetails.rejected, (state, action) => {
        state.accounts.loading = false; state.accounts.error = action.error.message;
      });
    // Savings
    builder
      .addCase(fetchSavings.pending, (state) => { state.savings.loading = true; })
      .addCase(fetchSavings.fulfilled, (state, action) => {
        state.savings.loading = false;
        state.savings.list = action.payload.list || [];
        state.savings.total = action.payload.total || 0;
      })
      .addCase(fetchSavings.rejected, (state, action) => {
        state.savings.loading = false; state.savings.error = action.error.message;
      })
      .addCase(fetchSavingDetails.pending, (state) => { state.savings.loading = true; })
      .addCase(fetchSavingDetails.fulfilled, (state, action) => {
        state.savings.loading = false; state.savings.details = action.payload;
      })
      .addCase(fetchSavingDetails.rejected, (state, action) => {
        state.savings.loading = false; state.savings.error = action.error.message;
      });
    // Budgets
    builder
      .addCase(fetchBudgets.pending, (state) => { state.budgets.loading = true; })
      .addCase(fetchBudgets.fulfilled, (state, action) => {
        state.budgets.loading = false;
        state.budgets.list = action.payload.list || [];
        state.budgets.total = action.payload.total || 0;
      })
      .addCase(fetchBudgets.rejected, (state, action) => {
        state.budgets.loading = false; state.budgets.error = action.error.message;
      })
      .addCase(fetchBudgetDetails.pending, (state) => { state.budgets.loading = true; })
      .addCase(fetchBudgetDetails.fulfilled, (state, action) => {
        state.budgets.loading = false; state.budgets.details = action.payload;
      })
      .addCase(fetchBudgetDetails.rejected, (state, action) => {
        state.budgets.loading = false; state.budgets.error = action.error.message;
      });
    // Investments
    builder
      .addCase(fetchInvestments.pending, (state) => { state.investments.loading = true; })
      .addCase(fetchInvestments.fulfilled, (state, action) => {
        state.investments.loading = false;
        state.investments.list = action.payload.list || [];
        state.investments.total = action.payload.total || 0;
      })
      .addCase(fetchInvestments.rejected, (state, action) => {
        state.investments.loading = false; state.investments.error = action.error.message;
      })
      .addCase(fetchInvestmentDetails.pending, (state) => { state.investments.loading = true; })
      .addCase(fetchInvestmentDetails.fulfilled, (state, action) => {
        state.investments.loading = false; state.investments.details = action.payload;
      })
      .addCase(fetchInvestmentDetails.rejected, (state, action) => {
        state.investments.loading = false; state.investments.error = action.error.message;
      });
    // Debts
    builder
      .addCase(fetchDebts.pending, (state) => { state.debts.loading = true; })
      .addCase(fetchDebts.fulfilled, (state, action) => {
        state.debts.loading = false;
        state.debts.list = action.payload.list || [];
        state.debts.total = action.payload.total || 0;
      })
      .addCase(fetchDebts.rejected, (state, action) => {
        state.debts.loading = false; state.debts.error = action.error.message;
      })
      .addCase(fetchDebtDetails.pending, (state) => { state.debts.loading = true; })
      .addCase(fetchDebtDetails.fulfilled, (state, action) => {
        state.debts.loading = false; state.debts.details = action.payload;
      })
      .addCase(fetchDebtDetails.rejected, (state, action) => {
        state.debts.loading = false; state.debts.error = action.error.message;
      });
    // Mortgages
    builder
      .addCase(fetchMortgages.pending, (state) => { state.mortgages.loading = true; })
      .addCase(fetchMortgages.fulfilled, (state, action) => {
        state.mortgages.loading = false;
        state.mortgages.list = action.payload.list || [];
        state.mortgages.total = action.payload.total || 0;
      })
      .addCase(fetchMortgages.rejected, (state, action) => {
        state.mortgages.loading = false; state.mortgages.error = action.error.message;
      })
      .addCase(fetchMortgageDetails.pending, (state) => { state.mortgages.loading = true; })
      .addCase(fetchMortgageDetails.fulfilled, (state, action) => {
        state.mortgages.loading = false; state.mortgages.details = action.payload;
      })
      .addCase(fetchMortgageDetails.rejected, (state, action) => {
        state.mortgages.loading = false; state.mortgages.error = action.error.message;
      });
  },
});

export default historySlice.reducer;